import { useFormik } from 'formik'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import * as Yup from 'yup'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import Loader from '../../../components/Loader'
import PageHeader from '../../../components/PageHeader'
import Message from '../../../components/Message'
import Checkbox from '../../../components/v2/buttons/Checkbox'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import RadioButton from '../../../components/v2/buttons/RadioButton'
import Dropdown from '../../../components/v2/Dropdown'
import Input from '../../../components/v2/Input'
import Title from '../../../components/v2/Title'
import UploadFile from '../../../components/v2/UploadFile'
import { RootState, useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { AuthState } from '../../../store/authSlice'
import {
  IntTplState,
  setCheckMyCar,
  setUpdateCarCountdown,
  setVehicleField,
} from '../../../store/internationalTplSlice'
import { TplCarInfoResponse } from '../../../domain/types'
import { useInternationalTplPages } from './useInternationalTplPages'
import { useInternationalTplProgress } from './useInternationalTplProgress'

const InternationalTplCarInfo = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  const { checkout, filter } = useAppSelector((data: { internationalTpl: IntTplState }) => data.internationalTpl)

  // Use the hook to get pages and page data from Redux
  const { pages: pagesFromHook, isLoading: isPagesLoading } = useInternationalTplPages()
  const { totalCount, passedCount, pages: progressPages } = useInternationalTplProgress()
  const displayPages = progressPages.length > 0 ? progressPages : pagesFromHook

  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetIntTplPageInfoQuery('international-tpl-car-info')

  const { data: countries, isLoading: isCountriesLoading } = api.useGetCountriesQuery(undefined)
  const [getVehicleOptions, { data: vehicleOptions, isLoading: isVehicleOptionsLoading }] =
    api.useGetIntTplVehicleOptionMutation()
  const [getClientTypes, { data: client, isLoading: isClientLoading }] = api.useGetIntTplClientTypeMutation()
  const [getVehicleModels, { data: vehicleModels, isLoading: isVehicleModelsLoading }] =
    api.useGetIntTplVehicleModelsMutation()
  const checkMyCar = checkout.checkMyCar ?? false
  const carCountdown = checkout.carCountdown ?? { minute: 0, second: 0 }
  const [tplCarInfo, setTplCarInfo] = useState<TplCarInfoResponse | undefined>(undefined)
  const lastRequestKeyRef = useRef<string | null>(null)

  const _clientTypeId =
    filter.isoCode === 'AM'
      ? client?.clientTypes?.find((x) => x?.slug === checkout?.user?.clientType)?.id
      : client?.clientTypes?.find((x) => x?.slug === 'individual')?.id

  const _vehicleDocSidesCount = vehicleOptions?.vehicleDocumentTypes?.find(
    (x) => x.id === checkout?.vehicle?.documentTypeId,
  )?.neededSides

  const getCityzenshipIso = useCallback(
    (cityzenship: any): string | undefined => {
      if (!cityzenship) return undefined
      if (typeof cityzenship === 'string') return cityzenship
      if (typeof cityzenship === 'number' && countries) {
        const country = countries.find((c) => c.id === cityzenship)
        return country?.isoCode
      }
      return undefined
    },
    [countries],
  )

  useEffect(() => {
    // get client types
    if (filter?.isoCode && filter.day) {
      const _getClientTypesRequest = async () => {
        try {
          await getClientTypes({ countryCode: filter.isoCode, insuranceDays: filter.day as number }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (_getClientTypesRequest) (single)')
          }
        }
      }
      // call request
      _getClientTypesRequest()
    }
    return () => {}
  }, [filter, getClientTypes])

  useEffect(() => {
    // get vehicle options
    if (filter?.isoCode && filter.day && _clientTypeId) {
      const _geVehicleOptionRequest = async () => {
        try {
          await getVehicleOptions({
            countryCode: filter.isoCode,
            insuranceDays: filter.day as number,
            clientTypeId: _clientTypeId,
          }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (_geVehicleOptionRequest) (single)')
          }
        }
      }
      // call request
      _geVehicleOptionRequest()
    }
    return () => {}
  }, [filter, getVehicleOptions, _clientTypeId])

  useEffect(() => {
    // get vehicle models
    if (filter?.isoCode && checkout?.vehicle?.vehicleId) {
      const _geVehicleModelsRequest = async () => {
        try {
          await getVehicleModels({
            countryCode: filter.isoCode,
            vehicleMarkId: checkout.vehicle.vehicleId as number,
          }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (_geVehicleModelsRequest) (single)')
          }
        }
      }
      // call request
      _geVehicleModelsRequest()
    }
    return () => {}
  }, [filter, getVehicleModels, checkout.vehicle.vehicleId])

  const CURRENT_YEAR = useMemo(() => new Date().getFullYear(), [])

  const ValidationSchema = useMemo(
    () =>
      Yup.object({
        registrationCountry: Yup.string().required(`${t('required')}`),
        registrationNumber: Yup.string()
          .matches(/^\S+$/, `${t('spaceValidation')}`)
          .required(`${t('required')}`),
        codeType: Yup.string().required(`${t('required')}`),
        code: Yup.string()
          .matches(/^\S+$/, `${t('spaceValidation')}`)
          .required(`${t('required')}`),
        usageId: Yup.number()
          .typeError(`${t('required')}`)
          .required(`${t('required')}`),
        vehicleId: Yup.number()
          .typeError(`${t('required')}`)
          .required(`${t('required')}`),
        vehicleTypeId: Yup.number()
          .typeError(`${t('required')}`)
          .required(`${t('required')}`),
        vehicleModelId: Yup.number()
          .typeError(`${t('required')}`)
          .required(`${t('required')}`),
        manufactureYear: Yup.string()
          .matches(/^\d{4}$/, t('enterValidYear'))
          .min(4, t('minFourSymbolos'))
          .max(4, t('minFourSymbolos'))
          .test('valid-year-range', t('enterValidYear'), (value) => {
            if (!value) return false
            const year = Number(value)
            return year >= 1900 && year <= CURRENT_YEAR
          })
          .required(`${t('required')}`),
        engineCapacity: Yup.string()
          .matches(/^\d+(\.\d+)?$/, `${t('required')}`)
          .required(`${t('required')}`),
        color: Yup.string()
          .matches(/^\S+(?:.*\S)?$/, `${t('required')}`)
          .required(`${t('required')}`),
        documentTypeId: Yup.number()
          .typeError(`${t('required')}`)
          .required(`${t('required')}`),
        documentNumber: Yup.string()
          .matches(/^\S+$/, `${t('spaceValidation')}`)
          .required(`${t('required')}`),
        vehicleDocOneSide: Yup.string().test('vehicleDocOneSide-required', t('required'), function (value) {
          const { documentTypeId } = this.parent
          const n = vehicleOptions?.vehicleDocumentTypes?.find((d) => d.id === documentTypeId)?.neededSides

          if (n === undefined) return true
          if (n >= 1) return !!value
          return true
        }),
        vehicleDocSecondSide: Yup.string().test('vehicleDocSecondSide-required', t('required'), function (value) {
          const { documentTypeId } = this.parent
          const n = vehicleOptions?.vehicleDocumentTypes?.find((d) => d.id === documentTypeId)?.neededSides

          if (n === undefined) return true
          if (n >= 2) return !!value
          return true
        }),
      }),
    [CURRENT_YEAR, t, vehicleOptions],
  )

  const initialValues = {
    registrationCountry: checkout.vehicle.registrationCountry || '',
    registrationNumber: checkout.vehicle.registrationNumber || '',
    codeType: checkout.vehicle.codeType || 'vin',
    code: checkout.vehicle.code || '',
    usageId: checkout.vehicle.usageId || '',
    vehicleId: checkout.vehicle.vehicleId || '',
    vehicleTypeId: checkout.vehicle.vehicleTypeId || '',
    vehicleModelId: checkout.vehicle.vehicleModelId || '',
    manufactureYear: checkout.vehicle.manufactureYear || '',
    engineCapacity: checkout.vehicle.engineCapacity || '',
    color: checkout.vehicle.color || '',
    documentTypeId: checkout.vehicle.documentTypeId || '',
    documentNumber: checkout.vehicle.documentNumber || '',
    vehicleDocOneSide: checkout.vehicle.vehicleDocOneSide || '',
    vehicleDocSecondSide: checkout.vehicle.vehicleDocSecondSide || '',
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: ValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: () => {},
  })

  const { setFieldValue, values: formValues } = formik

  type VehicleFormKey = keyof typeof initialValues

  const handleVehicleFieldChange = useCallback(
    <K extends VehicleFormKey>(field: K) =>
      (value: any) => {
        dispatch(
          setVehicleField({
            fieldName: field,
            fieldValue: value,
          }),
        )
        setFieldValue(field, value)
      },
    [dispatch, setFieldValue],
  )

  const handleVehicleDropdownChange = useCallback(
    <K extends VehicleFormKey>(field: K) =>
      (option: { value: any } | null) => {
        const selectedValue = option?.value ?? ''
        dispatch(
          setVehicleField({
            fieldName: field,
            fieldValue: selectedValue,
          }),
        )
        setFieldValue(field, selectedValue)
        formik.setFieldTouched(field, true, true)

        if (field === 'usageId') {
          dispatch(setVehicleField({ fieldName: 'vehicleTypeId', fieldValue: '' }))
          setFieldValue('vehicleTypeId', '')
          formik.setFieldTouched('vehicleTypeId', true, true)
        }
      },
    [dispatch, formik, setFieldValue],
  )

  const personalIdNumber = checkout.user.idNumber
  const isGeorgiaCar = (formValues.registrationCountry || '').toLowerCase() === 'ge'
  const isIndividualClient = checkout.user.clientType === 'individual'
  const isGeCitizen = getCityzenshipIso(checkout.user.cityzenship)?.toLowerCase() === 'ge'
  const canAutoFill =
    isIndividualClient &&
    isGeCitizen &&
    isGeorgiaCar &&
    !!personalIdNumber &&
    personalIdNumber.length === 11 &&
    !!formValues.registrationNumber
  const requestKey = `${personalIdNumber ?? ''}:${formValues.registrationNumber ?? ''}`

  const [fetchTplCarInfo, { data: tplCarInfoResponse }] = api.useLazyGetTplCarInfoQuery()

  const setCarInfoFromApi = useCallback(() => {
    if (!tplCarInfo?.registrationNumber) return
    if (
      !formValues.registrationNumber ||
      formValues.registrationNumber.toLowerCase() !== tplCarInfo.registrationNumber.toLowerCase()
    ) {
      return
    }

    handleVehicleFieldChange('registrationNumber')(tplCarInfo.registrationNumber)
    dispatch(setCheckMyCar(true))

    if (tplCarInfo.vinCode) {
      handleVehicleFieldChange('codeType')('vin')
      handleVehicleFieldChange('code')(tplCarInfo.vinCode)
    }

    if (tplCarInfo.engineCapacity) {
      handleVehicleFieldChange('engineCapacity')(tplCarInfo.engineCapacity.toString())
    }

    if (tplCarInfo.releasedAt) {
      handleVehicleFieldChange('manufactureYear')(tplCarInfo.releasedAt.toString())
    }

    if (tplCarInfo.brand && vehicleOptions?.vehicleMarks?.length) {
      const brandMatch = vehicleOptions.vehicleMarks.find(
        (item) => item.name?.toLowerCase() === tplCarInfo.brand.toLowerCase(),
      )
      if (brandMatch?.id) {
        handleVehicleFieldChange('vehicleId')(brandMatch.id)
      }
    }

    if (tplCarInfo.type && vehicleOptions?.vehicleTypes?.length) {
      const normalizedType = tplCarInfo.type.toLowerCase()
      const typeMatch = vehicleOptions.vehicleTypes.find((item) =>
        Object.values(item.translations || {}).some(
          (translation) => translation?.name?.toLowerCase() === normalizedType,
        ),
      )
      if (typeMatch?.ligaApiId) {
        handleVehicleFieldChange('vehicleTypeId')(typeMatch.ligaApiId)
      }
    }
  }, [dispatch, formValues.registrationNumber, handleVehicleFieldChange, tplCarInfo, vehicleOptions])

  const handleCarInfoRequest = useCallback(() => {
    if (!canAutoFill) return
    if (tplCarInfo) return
    if (lastRequestKeyRef.current === requestKey) return
    // Guard against StrictMode/event replays: ensure one request per key.
    lastRequestKeyRef.current = requestKey
    dispatch(setUpdateCarCountdown({ minute: 0, second: 30 }))
    fetchTplCarInfo({
      privateNumber: personalIdNumber ?? '',
      registrationNumber: formValues.registrationNumber || '',
      relatedPrivateNumber: null,
    })
  }, [canAutoFill, dispatch, fetchTplCarInfo, formValues.registrationNumber, personalIdNumber, requestKey, tplCarInfo])

  useEffect(() => {
    setCarInfoFromApi()
  }, [setCarInfoFromApi])

  useEffect(() => {
    if (tplCarInfoResponse) {
      setTplCarInfo(tplCarInfoResponse)
    }
  }, [tplCarInfoResponse])

  useEffect(() => {
    if (carCountdown.minute === 0 && carCountdown.second === 0) return

    const intervalId = setInterval(() => {
      if (carCountdown.minute === 0 && carCountdown.second <= 1) {
        dispatch(setUpdateCarCountdown({ minute: 0, second: 0 }))
        clearInterval(intervalId)
        return
      }

      if (carCountdown.second === 0) {
        dispatch(setUpdateCarCountdown({ minute: carCountdown.minute - 1, second: 59 }))
      } else {
        dispatch(setUpdateCarCountdown({ minute: carCountdown.minute, second: carCountdown.second - 1 }))
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [carCountdown.minute, carCountdown.second, dispatch])

  useEffect(() => {
    if (!tplCarInfo?.model || !vehicleModels?.length) return
    if (formValues.vehicleModelId) return
    const modelMatch = vehicleModels.find((item) => item.name?.toLowerCase() === tplCarInfo.model.toLowerCase())
    if (modelMatch?.id) {
      handleVehicleFieldChange('vehicleModelId')(modelMatch.id)
    }
  }, [formValues.vehicleModelId, handleVehicleFieldChange, tplCarInfo, vehicleModels])

  const handleFieldBlur = useCallback(
    (field: VehicleFormKey) => () => formik.setFieldTouched(field, true, true),
    [formik],
  )

  const markAllFieldsTouched = useCallback(() => {
    const touchedFields = Object.keys(formValues).reduce((acc, key) => {
      acc[key as VehicleFormKey] = true
      return acc
    }, {} as Record<VehicleFormKey, boolean>)
    formik.setTouched(touchedFields, true)
  }, [formValues, formik])

  const handleNext = useCallback(async () => {
    const errors = await formik.validateForm()
    if (Object.keys(errors).length > 0) {
      markAllFieldsTouched()
      return
    }
    handleVehicleFieldChange('codeType')(checkout.vehicle.codeType || 'vin')

    if (auth && auth.user && auth.token && !auth.userLoading) {
      navigate('/international-tpl-offer')
    } else {
      navigate('/international-tpl-user')
    }
  }, [auth, formik, markAllFieldsTouched, navigate, handleVehicleFieldChange, checkout.vehicle.codeType])

  const _vehicleCodeTypes = [
    {
      code: 'vin',
      translation: {
        ge: {
          name: 'VIN',
        },
        en: {
          name: 'VIN',
        },
      },
    },
    {
      code: 'chassisNumber',
      translation: {
        ge: {
          name: 'შასის ნომერი',
        },
        en: {
          name: 'Chassis number',
        },
      },
    },
    {
      code: 'bodyNumber',
      translation: {
        ge: {
          name: 'კორპუსის ნომერი',
        },
        en: {
          name: 'Body Number',
        },
      },
    },
  ]

  // auto redirect first page
  useEffect(() => {
    if (!filter?.isoCode) {
      navigate('/international-tpl-country')
    }
    return () => {}
  }, [filter?.isoCode, navigate])

  const timerIsVisible = !(carCountdown.minute === 0 && carCountdown.second === 0)
  const formattedTime =
    carCountdown.minute > 0
      ? `${String(carCountdown.minute).padStart(2, '0')}:${String(carCountdown.second).padStart(2, '0')}`
      : `${String(carCountdown.second).padStart(2, '0')}`

  const isAutoFillEligible = isIndividualClient && isGeCitizen && isGeorgiaCar && checkout.user.isOwner

  return isPageInfoLoading || isPagesLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <PageHeader total={totalCount} passed={passedCount} pages={displayPages} />
      <Container>
        <Content>
          <Title text={pageInfo?.translations?.[lang]?.title} />
          <Block>
            <Dropdown
              style={{ width: '100%' }}
              isLoading={isCountriesLoading}
              title={`${t('countryOfRegistration')}`}
              isSearchable={true}
              isDisabled={!(countries && countries.length >= 1)}
              value={formik.values.registrationCountry}
              options={
                countries
                  ? [
                      ...countries
                        ?.filter((c) => c.isoCode.toLowerCase() !== filter?.isoCode?.toLowerCase())
                        ?.map((c) => ({
                          value: c?.isoCode,
                          label: c?.translations?.[lang]?.name,
                        })),
                    ]
                  : []
              }
              onChange={handleVehicleDropdownChange('registrationCountry')}
              error={!!(formik.touched.registrationCountry && formik.errors.registrationCountry)}
              onBlur={handleFieldBlur('registrationCountry')}
            />
            <CounterBlock
              style={{
                opacity: timerIsVisible ? '0.8' : 'unset',
              }}
            >
              <Input
                id="vehicle-plate-number"
                name="registrationNumber"
                label={`${t('registerNumber')}`}
                type="text"
                placeholder=""
                value={formik.values.registrationNumber || ''}
                onChange={(value) => {
                  if (checkMyCar) {
                    dispatch(setCheckMyCar(false))
                  }
                  setTplCarInfo(undefined)
                  lastRequestKeyRef.current = null
                  dispatch(setUpdateCarCountdown({ minute: 0, second: 0 }))
                  handleVehicleFieldChange('registrationNumber')(value)
                }}
                width={'fullWidth'}
                error={!!(formik.touched.registrationNumber && formik.errors.registrationNumber)}
                errorText={formik.touched.registrationNumber ? (formik.errors.registrationNumber as string) : undefined}
                onBlur={formik.handleBlur}
                disabled={timerIsVisible}
              />
              <CounterDiv>{timerIsVisible ? <CounterSpan>{formattedTime}</CounterSpan> : null}</CounterDiv>
            </CounterBlock>
            {isAutoFillEligible ? (
              <>
                <Checkbox
                  id="check-int-tpl-car-info"
                  label={t('acceptToCheckCarInfo')}
                  defaultChecked={checkMyCar}
                  checked={checkMyCar}
                  disabled={timerIsVisible || !isAutoFillEligible || personalIdNumber?.length !== 11}
                  style={{
                    opacity: timerIsVisible ? '0.8' : 'unset',
                  }}
                  onChange={(value: boolean) => {
                    dispatch(setCheckMyCar(value))
                    if (value) {
                      handleCarInfoRequest()
                    }
                    if (!value) {
                      dispatch(setUpdateCarCountdown({ minute: 0, second: 0 }))
                    }
                  }}
                />
                {tplCarInfo && !tplCarInfo.brand && !tplCarInfo.vinCode && checkMyCar ? (
                  <Message variant="gray" text={`${t('notFountCarInfoForRegistrationNumber')}`} />
                ) : null}
              </>
            ) : null}
            <VehicleCodeTypeBlock>
              <RadioButton
                size="small"
                defaultOption={formik.values.codeType || 'vin'}
                onChange={(value) => {
                  handleVehicleFieldChange('codeType')(value)
                  formik.setFieldTouched('codeType', true, true)
                }}
                options={[
                  ..._vehicleCodeTypes?.map((item) => ({
                    value: item.code,
                    label: item.translation?.[lang].name,
                  })),
                ]}
              />
            </VehicleCodeTypeBlock>
            {formik.values.codeType ? (
              <Input
                id="vehicle-usage-type"
                name="code"
                label={``}
                type="text"
                placeholder=""
                value={formik.values.code || ''}
                onChange={handleVehicleFieldChange('code')}
                width={'fullWidth'}
                error={!!(formik.touched.code && formik.errors.code)}
                errorText={formik.touched.code ? (formik.errors.code as string) : undefined}
                onBlur={formik.handleBlur}
              />
            ) : null}
            <Dropdown
              style={{ width: '100%' }}
              isLoading={isVehicleOptionsLoading}
              isDisabled={
                vehicleOptions && vehicleOptions.usageTypes && vehicleOptions.usageTypes.length >= 1 ? false : true
              }
              title={`${t('usageType')}`}
              isSearchable={true}
              value={formik.values.usageId}
              options={
                vehicleOptions?.usageTypes
                  ? vehicleOptions.usageTypes.map((item) => ({
                      value: item.ligaApiId,
                      label: item.translations?.[lang]?.name,
                    }))
                  : []
              }
              onChange={handleVehicleDropdownChange('usageId')}
              error={!!(formik.touched.usageId && formik.errors.usageId)}
              onBlur={handleFieldBlur('usageId')}
            />
            {formik.values.usageId ? (
              <Dropdown
                key={`vehicle-type-dropdown-${formik.values.usageId}`}
                style={{ width: '100%' }}
                isLoading={isVehicleOptionsLoading}
                title={`${t('carType')}`}
                isSearchable={true}
                isDisabled={
                  vehicleOptions && vehicleOptions.vehicleTypes && vehicleOptions.vehicleTypes.length >= 1
                    ? false
                    : true
                }
                value={formik.values.vehicleTypeId}
                options={
                  vehicleOptions?.vehicleTypes
                    ? vehicleOptions.vehicleTypes
                        .filter((item) =>
                          item.usageTypeIds?.includes(
                            vehicleOptions.usageTypes?.find((u) => u.ligaApiId === formik.values.usageId)?.id ?? -1,
                          ),
                        )
                        .map((item) => ({
                          value: item.ligaApiId,
                          label: item.translations?.[lang]?.name,
                        }))
                    : []
                }
                onChange={handleVehicleDropdownChange('vehicleTypeId')}
                error={!!(formik.touched.vehicleTypeId && formik.errors.vehicleTypeId)}
                onBlur={handleFieldBlur('vehicleTypeId')}
              />
            ) : null}
            <Dropdown
              style={{ width: '100%' }}
              isLoading={isVehicleOptionsLoading}
              title={`${t('brand')}`}
              isSearchable={true}
              isDisabled={
                vehicleOptions && vehicleOptions.vehicleMarks && vehicleOptions.vehicleMarks.length >= 1 ? false : true
              }
              value={formik.values.vehicleId}
              options={
                vehicleOptions?.vehicleMarks
                  ? vehicleOptions.vehicleMarks.map((item) => ({
                      value: item.id,
                      label: item?.name,
                    }))
                  : []
              }
              onChange={handleVehicleDropdownChange('vehicleId')}
              error={!!(formik.touched.vehicleId && formik.errors.vehicleId)}
              onBlur={handleFieldBlur('vehicleId')}
            />
            <Dropdown
              style={{ width: '100%' }}
              isLoading={isVehicleModelsLoading}
              title={`${t('carModel')}`}
              isSearchable={true}
              isDisabled={vehicleModels && vehicleModels.length >= 1 ? false : true}
              value={formik.values.vehicleModelId}
              options={
                vehicleModels
                  ? vehicleModels.map((item) => ({
                      value: item?.id,
                      label: item?.name,
                    }))
                  : []
              }
              onChange={handleVehicleDropdownChange('vehicleModelId')}
              error={!!(formik.touched.vehicleModelId && formik.errors.vehicleModelId)}
              onBlur={handleFieldBlur('vehicleModelId')}
            />
            <Input
              id="manufacture-year"
              name="manufactureYear"
              label={`${t('dateOfIssue')}`}
              type="number"
              placeholder="2025"
              value={formik.values.manufactureYear || ''}
              onChange={handleVehicleFieldChange('manufactureYear')}
              width={'fullWidth'}
              error={!!(formik.touched.manufactureYear && formik.errors.manufactureYear)}
              errorText={formik.touched.manufactureYear ? (formik.errors.manufactureYear as string) : undefined}
              onBlur={formik.handleBlur}
            />
            <Input
              id="engine-capacity"
              name="engineCapacity"
              label={`${t('engineCapacity')}`}
              type="number"
              placeholder="2.5 or 2500"
              value={formik.values.engineCapacity || ''}
              onChange={handleVehicleFieldChange('engineCapacity')}
              width={'fullWidth'}
              error={!!(formik.touched.engineCapacity && formik.errors.engineCapacity)}
              errorText={formik.touched.engineCapacity ? (formik.errors.engineCapacity as string) : undefined}
              onBlur={formik.handleBlur}
            />
            <Input
              id="vehicle-color"
              name="color"
              label={`${t('color')}`}
              type="text"
              placeholder=""
              value={formik.values.color || ''}
              onChange={handleVehicleFieldChange('color')}
              width={'fullWidth'}
              error={!!(formik.touched.color && formik.errors.color)}
              errorText={formik.touched.color ? (formik.errors.color as string) : undefined}
              onBlur={formik.handleBlur}
            />
            <hr style={{ width: '100%', margin: 0 }} />
            <div>
              <p style={{ textAlign: 'left' }}>{t('registrationDocument')}</p>
              <Dropdown
                style={{ width: '100%' }}
                isLoading={isVehicleOptionsLoading || isClientLoading}
                title={`${t('documentType')}`}
                isSearchable={true}
                isDisabled={
                  vehicleOptions &&
                  vehicleOptions.vehicleDocumentTypes &&
                  vehicleOptions.vehicleDocumentTypes.length >= 1
                    ? false
                    : true
                }
                value={formik.values.documentTypeId}
                options={
                  vehicleOptions?.vehicleDocumentTypes
                    ? vehicleOptions.vehicleDocumentTypes.map((item) => ({
                        value: item.id,
                        label: item.translations?.[lang]?.name,
                      }))
                    : []
                }
                onChange={handleVehicleDropdownChange('documentTypeId')}
                error={!!(formik.touched.documentTypeId && formik.errors.documentTypeId)}
                onBlur={handleFieldBlur('documentTypeId')}
              />
            </div>
            <Input
              id="vehicle-doc-number"
              name="documentNumber"
              label={`${t('documentNumber')}`}
              type="text"
              placeholder=""
              value={formik.values.documentNumber || ''}
              onChange={handleVehicleFieldChange('documentNumber')}
              width={'fullWidth'}
              error={!!(formik.touched.documentNumber && formik.errors.documentNumber)}
              errorText={formik.touched.documentNumber ? (formik.errors.documentNumber as string) : undefined}
              onBlur={formik.handleBlur}
            />
            {_vehicleDocSidesCount && _vehicleDocSidesCount >= 1 ? (
              <UploadFile
                label={t('document')}
                onChange={(value) => [
                  dispatch(
                    setVehicleField({
                      fieldName: 'vehicleDocOneSide',
                      fieldValue: value,
                    }),
                  ),
                  formik.setFieldValue('vehicleDocOneSide', value),
                  formik.setFieldTouched('vehicleDocOneSide', true, true),
                ]}
                defaultFileName={checkout.vehicle.vehicleDocOneSide ?? ''}
                errorMessage={
                  formik.touched.vehicleDocOneSide && formik.errors.vehicleDocOneSide
                    ? (formik.errors.vehicleDocOneSide as string)
                    : undefined
                }
              />
            ) : null}
            {_vehicleDocSidesCount && _vehicleDocSidesCount >= 2 ? (
              <UploadFile
                label={t('document')}
                onChange={(value) => [
                  dispatch(
                    setVehicleField({
                      fieldName: 'vehicleDocSecondSide',
                      fieldValue: value,
                    }),
                  ),
                  formik.setFieldValue('vehicleDocSecondSide', value),
                  formik.setFieldTouched('vehicleDocSecondSide', true, true),
                ]}
                defaultFileName={checkout.vehicle.vehicleDocSecondSide ?? ''}
                errorMessage={
                  formik.touched.vehicleDocSecondSide && formik.errors.vehicleDocSecondSide
                    ? (formik.errors.vehicleDocSecondSide as string)
                    : undefined
                }
              />
            ) : null}
          </Block>

          <Buttons>
            <NavigationButton
              text={`${t('back')}`}
              buttonType="prev"
              onClick={() => navigate('/international-tpl-personal-info')}
            />
            <NavigationButton text={`${t('next')}`} buttonType="next" disabled={!formik.isValid} onClick={handleNext} />
          </Buttons>
        </Content>
      </Container>
    </Wrapper>
  )
}

const VehicleCodeTypeBlock = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-block: 5px;
  row-gap: 10px;
`
const CounterDiv = styled.div`
  background-color: transparent;
  padding: 5px;
  position: absolute;
  top: 36px;
  right: 23px;
  font-size: 12px;
  color: ${Colors.primaryBlack};
  user-select: none;
  pointer-events: none;
`
const CounterSpan = styled.span`
  background-color: ${Colors.primaryRed};
  color: ${Colors.white};
  padding-inline: 5px;
  min-width: 30px;
  height: 30px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: -6px;
  margin-right: -6px;

  ${Responsive.mobile} {
    margin-top: -6px;
    margin-right: -19px;
  }
`
const CounterBlock = styled.div`
  position: relative;
`

const Content = styled.div`
  margin-top: 40px;
  margin-bottom: 32px;
  z-index: 2;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  ${Responsive.mobile} {
    margin-top: 24px;
  }
`
const Block = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  max-width: 432px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  background-color: ${Colors.white};
  border-radius: 8px;
  padding: 20px 32px;
`
const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;
`

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`

export default InternationalTplCarInfo
